import { useMemo } from 'react';
import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { DefaultButton as TooltipButton } from '#/components/LegacyTooltip/LegacyTooltip.styled';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';
import type {
  DateString,
  IanaTimezone,
  TimeString,
} from '#/types/TypedDateString';
import { offsetDateAndTimeStrings } from '#/utils/string/datetime/convertDatetimeString';
import { humanDate } from '#/utils/string/datetime/dateString';
import { shortIanaTimezone } from '#/utils/string/datetime/ianaTimezone';
import { humanTime } from '#/utils/string/datetime/timeString';

const StyledTooltipButton = styled(TooltipButton)`
  color: ${colors.lightContentGrey};

  &:hover {
    color: ${colors.brandBlue};
  }
`;

type TimeTooltipProps = {
  date: DateString;
  startTime: TimeString;
  endTime: TimeString | null;
  fromTimezone: IanaTimezone;
  toTimezone: IanaTimezone;
};

export function ListingDateAndTimeTimezoneTooltip({
  date,
  startTime,
  endTime,
  fromTimezone,
  toTimezone,
}: TimeTooltipProps) {
  const { locale } = useI18n();

  const newStart = useMemo(
    () =>
      offsetDateAndTimeStrings({
        date,
        time: startTime,
        fromTimezone,
        toTimezone,
      }),
    [date, startTime, fromTimezone, toTimezone],
  );

  const newEnd = useMemo(
    () =>
      endTime &&
      offsetDateAndTimeStrings({
        date,
        time: endTime,
        fromTimezone,
        toTimezone,
      }),
    [date, endTime, fromTimezone, toTimezone],
  );

  return (
    <Box ml="8px" mr="4px">
      <Tooltip
        variant="filled"
        content={
          <>
            {humanDate(newStart.date, { locale, monthFormat: 'long' })}{' '}
            {[
              humanTime(newStart.time, { locale }),
              newEnd && humanTime(newEnd.time, { locale }),
            ]
              .filter(Boolean)
              .join(' - ')}{' '}
            {shortIanaTimezone(toTimezone, { date, locale })}
          </>
        }
      >
        <StyledTooltipButton />
      </Tooltip>
    </Box>
  );
}
